<template>
  <div id="compromisso-modal-app">
    <modal
      elem-id="modal_crud_compromisso"
      modal-size="modal-md"
:close-label="t('Fechar')"
:save-label="t('Salvar compromisso')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
:title="t('Compromisso')"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
      >
      
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Action Buttons -->
        <div class="action-buttons">
          <Button
            v-if="shouldShowCancelar"
            @click="cancelarCompromisso"
            severity="danger"
            :label="t('Cancelar compromisso')"
            icon="pi pi-ban"
            data-testid="compromisso-modal-cancelar-button"
          />
          <Button
            v-if="shouldShowExcluir"
            @click="excluirCompromisso"
            severity="danger"
            :label="t('Excluir compromisso')"
            icon="pi pi-trash"
            data-testid="compromisso-modal-excluir-button"
          />
        </div>

        <!-- Tabbed Interface -->
        <Tabs v-if="isEditMode" v-model:value="activeTabIndex">
          <TabList>
            <Tab :value="0">{{ t('Compromisso') }}</Tab>
            <Tab :value="1">{{ t('Serviços') }}</Tab>
            <Tab v-if="mostraRecebimentos" :value="2" data-testid="compromisso-modal-tab-recebimentos">{{ t('Recebimentos') }}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel :value="0">
              <compromisso-form
                ref="formRef"
                :is-edit-mode="isEditMode"
                @save-success="handleSaveSuccess"
                @action-success="handleActionSuccess">
              </compromisso-form>
            </TabPanel>

            <TabPanel :value="1">
              <Button
                style="margin-top: 20px; margin-bottom: 20px;"
                id="btnAddServicoCompromisso"
                severity="primary"
                size="small"
                :label="t('Adicionar serviço')"
                icon="pi pi-plus"
                @click="novoCompromissoServico"
                data-testid="compromisso-modal-add-servico-button"
              />

              <!-- CompromissoServico components will be loaded here -->
              <compromisso-servico-table
                :compromisso-id="currentCompromissoId"
                ref="compromissoServicoTableRef"
                @edit-compromisso-servico="handleEditCompromissoServico">
              </compromisso-servico-table>
            </TabPanel>

            <TabPanel v-if="mostraRecebimentos" :value="2">
              <Button
                style="margin-top: 20px; margin-bottom: 20px;"
                id="btnAddRecebimentoCompromisso"
                severity="primary"
                size="small"
                :label="t('Adicionar recebimento')"
                icon="pi pi-plus"
                @click="novoRecebimento"
                data-testid="compromisso-modal-add-recebimento-button"
              />

              <!-- Recebimentos table -->
              <recebimento-table-compromisso
                :compromisso-id="currentCompromissoId"
                ref="recebimentoTableRef"
                @edit-recebimento="handleEditRecebimento">
              </recebimento-table-compromisso>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </template>
    </modal>
  </div>

  <!-- CompromissoServicoModal and RecebimentoModal are centralized in ModalManager.vue - opened via eventBus -->
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useCompromisso } from '../../composables/useCompromisso';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { veOsRecebimentosDoCompromisso } from '../../utils/recebimentosDoCompromisso';
import CompromissoForm from './CompromissoForm.vue';
import CompromissoServicoTable from '../compromissoservico/CompromissoServicoTable.vue';
// CompromissoServicoModal and RecebimentoModal are centralized in ModalManager.vue
import RecebimentoTableCompromisso from '../recebimento/RecebimentoTableCompromisso.vue';

type Id = number | string;

interface CompromissoFormData {
  compromisso_id?: number;
  compromisso_status?: string;
  compromisso_franquia_id?: Id;
  compromisso_orcamento_id?: Id;
  [key: string]: unknown;
}

interface CompromissoFormInstance {
  canSave: boolean;
  loadForEdit: (id: Id) => Promise<void>;
  saveCompromisso: () => Promise<void>;
  resetForm: () => void;
  getCompromissoData: () => CompromissoFormData;
}

interface TableInstance {
  loadData: () => void;
  initializeFilters?: () => Promise<void>;
}

interface ModalOpenPayload {
  id?: Id;
}

interface CheckoutPayload {
  id?: Id;
  compromissoId?: Id;
}

interface Props {
  usuarioVersao?: number | string;
}

const props = withDefaults(defineProps<Props>(), {
  usuarioVersao: 0
});

// Recebimentos do compromisso: quem vê o financeiro, ou a conta em Versão 1
const mostraRecebimentos = computed<boolean>(() => veOsRecebimentosDoCompromisso(props.usuarioVersao));

// Get i18n composable
const { t } = useI18n();

// Get confirm action composable (replaces direct useConfirm usage)
const { confirmAction } = useConfirmAction();

// Get compromisso composable
const {
  cancelarCompromisso: cancelarCompromissoAPI,
  removerCompromisso: removerCompromissoAPI,
  finalizarCompromisso: finalizarCompromissoAPI
} = useCompromisso();

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const formRef = ref<CompromissoFormInstance | null>(null);
const currentCompromissoId = ref<number>(0);
const compromissoServicoTableRef = ref<TableInstance | null>(null);
const recebimentoTableRef = ref<TableInstance | null>(null);
const activeTab = ref<string>('compromisso'); // Default active tab (legacy)
const activeTabIndex = ref<number>(0); // PrimeVue tabs use index

// Computed properties
const shouldShowCancelar = computed<boolean>(() => {
  const status = formRef.value?.getCompromissoData()?.compromisso_status;
  return isEditMode.value && !!status && status !== 'Cancelado' && status !== 'Realizado';
});

const shouldShowExcluir = computed<boolean>(() => {
  const status = formRef.value?.getCompromissoData()?.compromisso_status;
  return isEditMode.value && !!status && status !== 'Realizado';
});

// Watchers
watch(activeTabIndex, async (newIndex) => {
  // When switching to servicos tab (index 1), initialize filters for the table
  if (newIndex === 1) {
    log('CompromissoModal', 'watch.activeTabIndex', 'Switching to servicos tab, initializing table filters...');
    // Use nextTick to ensure DOM is updated
    await nextTick();

    // Get the ref dynamically and trigger filter initialization
    const tableRef = compromissoServicoTableRef.value;
    if (tableRef && tableRef.initializeFilters) {
      log('CompromissoModal', 'watch.activeTabIndex', 'Calling initializeFilters on CompromissoServicoTable...');
      await tableRef.initializeFilters();
    } else {
      log('CompromissoModal', 'watch.activeTabIndex', 'CompromissoServicoTable ref not available yet');
    }
  }
});

// Methods
const loadForEdit = async (compromissoId: Id): Promise<void> => {
  log('CompromissoModal', 'loadForEdit', 'Opening edit modal for ID', compromissoId);
  isVisible.value = true;
  isEditMode.value = true;
  currentCompromissoId.value = Number(compromissoId);
  activeTab.value = 'compromisso'; // Reset to first tab (legacy)
  activeTabIndex.value = 0; // Reset to first tab

  // Wait for the form component to be rendered
  await nextTick();

  // Load compromisso data in form
  if (formRef.value) {
    await formRef.value.loadForEdit(compromissoId);
  }

  // Initialize compromisso servico data
  setTimeout(() => {
    log('CompromissoModal', 'loadForEdit', 'Initializing servico/recebimentos defaults');
    buscaInfosPadraoCompromissoServico();
    buscaInfosPadraoRecebimentosDoCompromisso();
  }, 1000);
};

// Define emits
const emit = defineEmits<{
  close: [];
}>();

const closeModal = (): void => {
  log('CompromissoModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentCompromissoId.value = 0;

  // Reset form
  if (formRef.value) {
    formRef.value.resetForm();
  }

  // Notify parent to hide modal (unmount via v-if)
  emit('close');
};

const handleSave = async (): Promise<void> => {
  log('CompromissoModal', 'handleSave', 'Delegating save to form');
  if (formRef.value) {
    await formRef.value.saveCompromisso();
  }
};

const handleSaveSuccess = (): void => {
  log('CompromissoModal', 'handleSaveSuccess', 'Save success, closing modal');
  closeModal();
};

const handleActionSuccess = (): void => {
  log('CompromissoModal', 'handleActionSuccess', 'Action success, closing modal');
  closeModal();
};

const cancelarCompromisso = (): void => {
  log('CompromissoModal', 'cancelarCompromisso', 'Attempting cancel');

  confirmAction({
    message: t('Tem certeza que deseja cancelar este compromisso?'),
    header: t('Confirmação'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      try {
        const compromissoData = formRef.value?.getCompromissoData();
        if (!compromissoData?.compromisso_id) return;

        await cancelarCompromissoAPI(compromissoData.compromisso_id);

        log('CompromissoModal', 'cancelarCompromisso', 'Canceled successfully');
        eventBus.emit('globalMessage.success', t('Compromisso cancelado com sucesso!'));
        closeModal();
        eventBus.emit('compromisso.saved');

      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('[CompromissoModal] Error canceling compromisso:', error);
        eventBus.emit('globalMessage.error', message);
      }
    }
  });
};

const excluirCompromisso = (): void => {
  log('CompromissoModal', 'excluirCompromisso', 'Attempting delete');

  confirmAction({
    message: t('Tem certeza que deseja excluir este compromisso? Esta ação não pode ser desfeita.'),
    header: t('Confirmação'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim, excluir'),
    rejectLabel: t('Cancelar'),
    onAccept: async () => {
      try {
        const compromissoData = formRef.value?.getCompromissoData();
        if (!compromissoData?.compromisso_id) return;

        await removerCompromissoAPI(compromissoData.compromisso_id);

        log('CompromissoModal', 'excluirCompromisso', 'Deleted successfully');
        eventBus.emit('globalMessage.success', t('Compromisso excluído com sucesso!'));
        closeModal();
        eventBus.emit('compromisso.deleted');

      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('[CompromissoModal] Error deleting compromisso:', error);
        eventBus.emit('globalMessage.error', message);
      }
    }
  });
};

const checkoutCompromisso = (compromissoId: Id): void => {
  log('CompromissoModal', 'checkoutCompromisso', 'Attempting checkout for ID', compromissoId);

  confirmAction({
    message: t('Tem certeza que deseja finalizar este compromisso?'),
    header: t('Confirmação'),
    icon: 'pi pi-question-circle',
    acceptLabel: t('Sim, finalizar'),
    rejectLabel: t('Cancelar'),
    onAccept: async () => {
      try {
        await finalizarCompromissoAPI(Number(compromissoId));

        log('CompromissoModal', 'checkoutCompromisso', 'Checkout success');
        eventBus.emit('globalMessage.success', t('Compromisso finalizado com sucesso!'));
        eventBus.emit('compromisso.saved');

      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('[CompromissoModal] Error checking out compromisso:', error);
        eventBus.emit('globalMessage.error', message);
      }
    }
  });
};

const novoCompromissoServico = (): void => {
  log('CompromissoModal', 'novoCompromissoServico', 'Opening novo servico via eventBus');
  const compromissoData = formRef.value?.getCompromissoData();
  const orcamentoId = compromissoData?.compromisso_orcamento_id || 0;
  const franquiaId = compromissoData?.compromisso_franquia_id || 0;

  eventBus.emit('modal.compromisso-servico.open', {
    compromissoId: currentCompromissoId.value,
    orcamentoId: orcamentoId,
    franquiaId: franquiaId
  });
};

const handleEditCompromissoServico = (compromissoServicoId: Id): void => {
  log('CompromissoModal', 'handleEditCompromissoServico', 'Opening edit servico via eventBus', compromissoServicoId);
  const compromissoData = formRef.value?.getCompromissoData();
  const franquiaId = compromissoData?.compromisso_franquia_id || 0;

  eventBus.emit('modal.compromisso-servico.open', {
    id: compromissoServicoId,
    compromissoId: currentCompromissoId.value,
    franquiaId: franquiaId
  });
};

const novoRecebimento = (): void => {
  const compromissoData = formRef.value?.getCompromissoData();

  log('CompromissoModal', 'novoRecebimento', 'Emitting eventBus to open recebimento wizard', {
    compromissoId: currentCompromissoId.value,
    franquiaId: compromissoData?.compromisso_franquia_id
  });

  eventBus.emit('recebimento-wizard.open', {
    compromissoId: currentCompromissoId.value || 0,
    franquiaId: compromissoData?.compromisso_franquia_id || 0
  });
};

const handleEditRecebimento = (recebimentoId: Id): void => {
  log('CompromissoModal', 'handleEditRecebimento', 'Opening edit recebimento via eventBus', recebimentoId);
  // Don't call modal directly via ref - let eventBus handle it
  // The table already emits 'recebimento.open-modal-edit' which the modal listens to
  // Direct ref call would cause the modal to open twice
};

const handleRecebimentoCreated = (recebimentoData?: unknown): void => {
  log('CompromissoModal', 'handleRecebimentoCreated', 'Recebimento created, refreshing table', recebimentoData);
  // Refresh the recebimentos table
  buscaInfosPadraoRecebimentosDoCompromisso();
};

const buscaInfosPadraoCompromissoServico = (): void => {
  log('CompromissoModal', 'buscaInfosPadraoCompromissoServico', 'Trigger loadData on table');
  // This will trigger the CompromissoServico table to load data
  if (compromissoServicoTableRef.value) {
    compromissoServicoTableRef.value.loadData();
  }
};

const buscaInfosPadraoRecebimentosDoCompromisso = (): void => {
  log('CompromissoModal', 'buscaInfosPadraoRecebimentosDoCompromisso', 'Trigger loadData on recebimentos table');
  // This will trigger the Recebimento table to load data
  if (recebimentoTableRef.value) {
    recebimentoTableRef.value.loadData();
  }
};

const resetForm = (): void => {
  log('CompromissoModal', 'resetForm', 'Reset form via formRef');

  // Delegate to form component
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

/**
 * CompromissoModal
 *
 * Lifecycle: Globally registered, always mounted in main.js
 * Event listeners: Listens to compromisso.editRequested event to open modal
 * Refactored: 2025-10-14
 */

// Standard modal open handler (for modal.compromisso.open event)
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('CompromissoModal', 'handleModalOpen', 'Standard modal open event received', data);

  // Edit-only: compromissos are created by the agenda wizard, never here.
  if (data?.id) {
    loadForEdit(data.id);
  }
};

// Checkout handler (for modal.compromisso.checkout event)
const handleCheckout = (data?: CheckoutPayload | Id): void => {
  log('CompromissoModal', 'handleCheckout', 'Checkout event received', data);
  let compromissoId: Id | undefined;
  if (typeof data === 'object' && data !== null) {
    compromissoId = data.id || data.compromissoId;
  } else {
    compromissoId = data;
  }
  if (compromissoId) {
    checkoutCompromisso(compromissoId);
  }
};

const handleEditRequested = (compromissoId?: Id): void => {
  log('CompromissoModal', 'event:compromisso.editRequested', 'Received', compromissoId);
  if (compromissoId) {
    loadForEdit(compromissoId);
  }
};

// Lifecycle
onMounted(() => {
  log('CompromissoModal', 'mounted', 'Component mounted');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.compromisso.open', handleModalOpen);
  eventBus.on('modal.compromisso.close', closeModal);
  eventBus.on('modal.compromisso.checkout', handleCheckout);

  // Legacy events (for backward compatibility)
  eventBus.on('compromisso.editRequested', handleEditRequested);
  eventBus.on('compromisso.checkout', handleCheckout);

  // Listen for recebimento created event
  eventBus.on('recebimento.created', handleRecebimentoCreated);
});

onBeforeUnmount(() => {
  log('CompromissoModal', 'beforeUnmount', 'Component unmounted');

  // Remove standard modal events
  eventBus.off('modal.compromisso.open', handleModalOpen);
  eventBus.off('modal.compromisso.close', closeModal);
  eventBus.off('modal.compromisso.checkout', handleCheckout);

  // Remove legacy events
  eventBus.off('compromisso.editRequested', handleEditRequested);
  eventBus.off('compromisso.checkout', handleCheckout);
  eventBus.off('recebimento.created', handleRecebimentoCreated);
});

// Expose methods for parent components
defineExpose({
  loadForEdit,
  closeModal,
  checkoutCompromisso,
  resetForm
});
</script>

<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
}

:deep(.p-tabs .p-tablist) {
  background: transparent;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 20px;
}

:deep(.p-tabs .p-tab) {
  padding: 0.75rem 1.25rem;
  font-weight: 500;
}

:deep(.p-tabs .p-tabpanels) {
  background: transparent;
  padding: 0;
}

:deep(.p-tabs .p-tabpanel) {
  padding-top: 1rem;
}
</style>